import React, { useState, useEffect, useRef } from "react";
import "../styles/recepcionista.css";
import { useRecepcionistaDashboard } from "../hooks/useRecepcionistaDashboard";
import OrigenTendenciaChart from "../../components/OrigenTendenciaChart";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "-"
    : d.toLocaleDateString("es-EC", { day: "2-digit", month: "short", year: "numeric" });
};

const formatMoney = (value) => {
  const n = Number(value);
  if (!n) return null;
  return new Intl.NumberFormat("es-EC", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(n);
};

const sortOrder = { disponible: 0, casi_lleno: 1, lleno: 2 };

function getDispInfo(cupo, capacidad) {
  if (cupo <= 0)
    return { key: "lleno",      color: "#dc2626", bg: "#fee2e2", barColor: "#ef4444" };
  const pct = capacidad ? ((capacidad - cupo) / capacidad) * 100 : 0;
  if (pct >= 80)
    return { key: "casi_lleno", color: "#d97706", bg: "#fef3c7", barColor: "#f59e0b" };
  return   { key: "disponible", color: "#059669", bg: "#d1fae5", barColor: "#10b981" };
}

function proximaParaCurso(cursoId, proximas) {
  return proximas
    .filter((p) => p.curso_id === cursoId && p.cupo_disponible > 0)
    .sort((a, b) => new Date(a.fecha_inicio) - new Date(b.fecha_inicio))[0] || null;
}


// ─── CursosActivosCarousel ───────────────────────────────────────────────────

function CursosActivosCarousel({ cursos, proximas }) {
  const scrollRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const CARD_W = 226; // card width + gap

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 2);
    setCanNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Pequeño timeout para que el DOM se pinte y scrollWidth sea correcto
    const t = setTimeout(updateArrows, 50);
    el.addEventListener("scroll", updateArrows, { passive: true });
    return () => { clearTimeout(t); el.removeEventListener("scroll", updateArrows); };
  }, [cursos]);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * CARD_W * 2, behavior: "smooth" });
  };

  return (
    <div style={{ position: "relative" }}>
      {canPrev && (
        <button className="recep-carousel-btn recep-carousel-btn--prev" onClick={() => scroll(-1)} aria-label="Anterior">
          <i className="bi bi-chevron-left" />
        </button>
      )}
      <div ref={scrollRef} className="recep-activos-scroll">
        {cursos.slice(0, 20).map((c) => (
          <CursoActivoCard key={c.id} curso={c} proximas={proximas} />
        ))}
      </div>
      {canNext && (
        <button className="recep-carousel-btn recep-carousel-btn--next" onClick={() => scroll(1)} aria-label="Siguiente">
          <i className="bi bi-chevron-right" />
        </button>
      )}
    </div>
  );
}

// ─── CursoBoardItem ──────────────────────────────────────────────────────────

function CursoBoardItem({ curso, proximas }) {
  const pct     = Math.min(curso.porcentaje, 100);
  const disp    = getDispInfo(curso.cupo_disponible, curso.capacidad);
  const proxima = disp.key === "lleno" ? proximaParaCurso(curso.curso_id, proximas) : null;

  const base       = Number(curso.precio_base) || 0;
  const descuento  = Number(curso.descuento_porcentaje) || 0;
  const precioFinal   = base > 0 ? formatMoney(base * (1 - descuento / 100) * 1.15) : null;
  const precioSinDesc = base > 0 && descuento > 0 ? formatMoney(base * 1.15) : null;

  return (
    <div className="recep-curso-item" style={{ borderLeft: `4px solid ${disp.barColor}` }}>
      {/* Fila: badge estado + promo */}
      <div className="d-flex justify-content-between align-items-start mb-1">
        <span
          className="badge rounded-pill"
          style={{
            background: curso.estado === "cursando" ? "#d1fae5" : "#e0f2fe",
            color:      curso.estado === "cursando" ? "#065f46" : "#0369a1",
            fontSize: "0.7rem",
          }}
        >
          {curso.estado === "cursando" ? "Cursando" : "Programado"}
        </span>
        <span className="text-muted" style={{ fontSize: "0.72rem" }}>{curso.promocion}</span>
      </div>

      {/* Fila: nombre del curso + cupo inline a la derecha */}
      <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
        <div className="recep-curso-nombre" style={{ flex: 1 }}>{curso.curso_nombre}</div>
        <div
          className="recep-cupo-badge flex-shrink-0"
          style={{
            color: disp.color,
            background: disp.bg,
            borderRadius: 10,
            padding: "0.25rem 0.55rem",
            textAlign: "center",
            minWidth: 44,
          }}
        >
          {disp.key === "lleno" ? (
            <span style={{ fontSize: "0.72rem", fontWeight: 700, lineHeight: 1.2, display: "block" }}>Sin<br />cupo</span>
          ) : (
            <>
              <div style={{ fontSize: "1.35rem", fontWeight: 800, lineHeight: 1 }}>{curso.cupo_disponible}</div>
              <div style={{ fontSize: "0.65rem", fontWeight: 600, lineHeight: 1.2 }}>
                {curso.cupo_disponible === 1 ? "cupo" : "cupos"}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="text-muted mb-2" style={{ fontSize: "0.78rem" }}>
        <i className="bi bi-calendar3 me-1" />
        {formatDate(curso.fecha_inicio)}
      </div>

      <div className="recep-ocp-bar mt-1">
        <div className="recep-ocp-fill" style={{ width: `${pct}%`, background: disp.barColor }} />
      </div>
      <div className="d-flex justify-content-between mt-1" style={{ fontSize: "0.72rem", color: "var(--mg-muted)" }}>
        <span>{curso.matriculados} matriculados</span>
        <span>{curso.capacidad} capacidad</span>
      </div>

      <div className="recep-curso-info mt-2">
        {precioFinal && (
          <span className="recep-info-item" style={descuento > 0 ? { background: "#fef3c7", color: "#92400e" } : {}}>
            <i className="bi bi-tag me-1" />
            {precioSinDesc && (
              <span style={{ textDecoration: "line-through", opacity: 0.55, marginRight: "0.3rem", fontWeight: 400 }}>
                {precioSinDesc}
              </span>
            )}
            {precioFinal}
            {descuento > 0 && (
              <span style={{ marginLeft: "0.3rem", fontSize: "0.68rem", fontWeight: 700 }}>
                -{descuento}%
              </span>
            )}
          </span>
        )}
        {Number(curso.monto_matricula) > 0 && (
          <span className="recep-info-item">
            <i className="bi bi-pencil-square me-1" />
            Mat. {formatMoney(curso.monto_matricula)}
          </span>
        )}
        {Number(curso.monto_min_reserva) > 0 && (
          <span className="recep-info-item recep-info-item--muted">
            <i className="bi bi-bookmark me-1" />
            Reserva {formatMoney(curso.monto_min_reserva)}
          </span>
        )}
        {Number(curso.duracion_horas) > 0 && (
          <span className="recep-info-item recep-info-item--muted">
            <i className="bi bi-clock me-1" />
            {curso.duracion_horas}h
          </span>
        )}
      </div>

      {proxima && (
        <div className="recep-proxima-hint mt-2">
          <i className="bi bi-arrow-right-circle me-1" />
          Proxima: <strong>{formatDate(proxima.fecha_inicio)}</strong>
          {" · "}{proxima.cupo_disponible} cupo{proxima.cupo_disponible !== 1 ? "s" : ""}
        </div>
      )}
      {disp.key === "lleno" && !proxima && (
        <div className="recep-proxima-hint recep-proxima-hint--sin mt-2">
          <i className="bi bi-calendar-x me-1" />
          Sin proximas fechas disponibles
        </div>
      )}
    </div>
  );
}

// ─── CursoActivoCard (tarjeta compacta para el strip horizontal) ─────────────

function CursoActivoCard({ curso, proximas }) {
  const pct    = Math.min(curso.porcentaje, 100);
  const disp   = getDispInfo(curso.cupo_disponible, curso.capacidad);
  const proxima = disp.key === "lleno" ? proximaParaCurso(curso.curso_id, proximas) : null;

  const base      = Number(curso.precio_base) || 0;
  const descuento = Number(curso.descuento_porcentaje) || 0;
  const precioFinal   = base > 0 ? formatMoney(base * (1 - descuento / 100) * 1.15) : null;
  const precioSinDesc = base > 0 && descuento > 0 ? formatMoney(base * 1.15) : null;

  return (
    <div className="recep-activo-card" style={{ borderTop: `3px solid ${disp.barColor}` }}>
      {/* Nombre + cupo */}
      <div className="d-flex justify-content-between align-items-start gap-1 mb-1">
        <div className="recep-activo-nombre">{curso.curso_nombre}</div>
        <div
          className="flex-shrink-0"
          style={{
            color: disp.color,
            background: disp.bg,
            borderRadius: 8,
            padding: "0.2rem 0.45rem",
            textAlign: "center",
            minWidth: 38,
          }}
        >
          {disp.key === "lleno" ? (
            <span style={{ fontSize: "0.65rem", fontWeight: 700, lineHeight: 1.2, display: "block" }}>Sin<br />cupo</span>
          ) : (
            <>
              <div style={{ fontSize: "1.1rem", fontWeight: 800, lineHeight: 1 }}>{curso.cupo_disponible}</div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600 }}>{curso.cupo_disponible === 1 ? "cupo" : "cupos"}</div>
            </>
          )}
        </div>
      </div>

      {/* Promo + fecha */}
      <div className="d-flex justify-content-between align-items-center mb-2" style={{ fontSize: "0.72rem", color: "var(--mg-muted)" }}>
        <span>{curso.promocion}</span>
        <span><i className="bi bi-calendar3 me-1" />{formatDate(curso.fecha_inicio)}</span>
      </div>

      {/* Barra ocupación */}
      <div className="recep-ocp-bar mb-1">
        <div className="recep-ocp-fill" style={{ width: `${pct}%`, background: disp.barColor }} />
      </div>
      <div className="d-flex justify-content-between mb-2" style={{ fontSize: "0.68rem", color: "var(--mg-muted)" }}>
        <span>{curso.matriculados} matr.</span>
        <span>{curso.capacidad} cap.</span>
      </div>

      {/* Precio */}
      {precioFinal && (
        <div className="recep-curso-info">
          <span className="recep-info-item" style={descuento > 0 ? { background: "#fef3c7", color: "#92400e" } : {}}>
            <i className="bi bi-tag me-1" />
            {precioSinDesc && (
              <span style={{ textDecoration: "line-through", opacity: 0.55, marginRight: "0.25rem", fontWeight: 400 }}>
                {precioSinDesc}
              </span>
            )}
            {precioFinal}
            {descuento > 0 && <span style={{ marginLeft: "0.25rem", fontSize: "0.65rem", fontWeight: 700 }}>-{descuento}%</span>}
          </span>
        </div>
      )}

      {/* Hint próxima fecha si está lleno */}
      {proxima && (
        <div className="recep-proxima-hint mt-2" style={{ fontSize: "0.7rem" }}>
          <i className="bi bi-arrow-right-circle me-1" />
          Próx.: <strong>{formatDate(proxima.fecha_inicio)}</strong>
        </div>
      )}
      {disp.key === "lleno" && !proxima && (
        <div className="recep-proxima-hint recep-proxima-hint--sin mt-2" style={{ fontSize: "0.7rem" }}>
          <i className="bi bi-calendar-x me-1" />Sin próximas fechas
        </div>
      )}
    </div>
  );
}

// ─── BoardSection ────────────────────────────────────────────────────────────

function BoardSection({ titulo, subtitulo, cursos, proximas, chipIcon, chipLabel, scrollable }) {
  return (
    <div className="card recep-section-card h-100">
      <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
        <div>
          <h6 className="mb-0">{titulo}</h6>
          <small className="text-muted">{subtitulo}</small>
        </div>
        <span className="dashboard-chip">
          <i className={`bi ${chipIcon} me-1`} />
          {chipLabel}
        </span>
      </div>
      <div className="p-3" style={scrollable ? { maxHeight: 500, overflowY: "auto" } : {}}>
        <div className="recep-board-grid">
          {cursos.map((c) => (
            <CursoBoardItem key={c.id} curso={c} proximas={proximas} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── RecepcionistaDashboard ──────────────────────────────────────────────────

export default function RecepcionistaDashboard() {
  const { cursos, proximas, sources, sourcesMonthly, lastUpdate, loading, fetchDashboard } =
    useRecepcionistaDashboard();

  const activos     = cursos.filter((c) => c.estado === "cursando");
  const programados = cursos.filter((c) => c.estado === "programada");

  const sortCursos = (arr) =>
    [...arr].sort((a, b) => sortOrder[a.disponibilidad] - sortOrder[b.disponibilidad]);

  const activosOrdenados     = sortCursos(activos);
  const programadosOrdenados = sortCursos(programados);

  const conCupo    = cursos.filter((c) => c.cupo_disponible > 0).length;
  const casiLlenos = cursos.filter((c) => c.cupo_disponible > 0 && c.porcentaje >= 80).length;
  const llenos     = cursos.filter((c) => c.cupo_disponible <= 0).length;

  const proximasSinInscritos = proximas.filter((p) => !cursos.some((c) => c.id === p.id));

  const formattedTime = lastUpdate
    ? new Date(lastUpdate).toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="dashboard-page management-page management-theme-blue recepcionista container-fluid py-4">

      {/* Header */}
      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">Recepcionista</p>
          <h3 className="management-title mb-2">Panel de atencion</h3>
          <div className="d-flex flex-wrap gap-2">
            {conCupo > 0 && (
              <span className="badge rounded-pill recep-pill recep-pill--verde">
                <i className="bi bi-check-circle-fill me-1" />
                {conCupo} con cupo
              </span>
            )}
            {casiLlenos > 0 && (
              <span className="badge rounded-pill recep-pill recep-pill--amber">
                <i className="bi bi-exclamation-circle-fill me-1" />
                {casiLlenos} casi lleno{casiLlenos !== 1 ? "s" : ""}
              </span>
            )}
            {llenos > 0 && (
              <span className="badge rounded-pill recep-pill recep-pill--rojo">
                <i className="bi bi-x-circle-fill me-1" />
                {llenos} lleno{llenos !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
        <div className="d-flex flex-column align-items-end gap-2">
          <button
            className="btn recep-refresh-btn"
            type="button"
            onClick={fetchDashboard}
            disabled={loading}
          >
            <i className="bi bi-arrow-repeat me-2" />
            {loading ? "Actualizando..." : "Actualizar"}
          </button>
          {formattedTime && (
            <small className="text-muted">Actualizado {formattedTime}</small>
          )}
        </div>
      </div>

      {/* ── Fila 50/50: Próximos a iniciar | Origen de interesados ── */}
      <div className="row g-3 mb-3">
        <div className="col-lg-6 d-flex flex-column">
          {programadosOrdenados.length > 0 ? (
            <BoardSection
              titulo="Proximos a iniciar"
              subtitulo="Promociones programadas"
              cursos={programadosOrdenados}
              proximas={proximas}
              chipIcon="bi-hourglass-split"
              chipLabel={`${programadosOrdenados.length}`}
              scrollable
            />
          ) : (
            <div className="card recep-section-card h-100">
              <div className="p-4 text-center text-muted d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 200 }}>
                <i className="bi bi-hourglass d-block mb-2" style={{ fontSize: "1.8rem", opacity: 0.4 }} />
                Sin cursos próximos con inscritos
              </div>
            </div>
          )}
        </div>

        <div className="col-lg-6 d-flex flex-column">
          <OrigenTendenciaChart sources={sources} sourcesMonthly={sourcesMonthly} />
        </div>
      </div>

      {/* ── Cursos activos: strip horizontal scrolleable ── */}
      {activosOrdenados.length > 0 && (
        <div className="card recep-section-card mb-3">
          <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
            <div>
              <h6 className="mb-0">Cursos activos</h6>
              <small className="text-muted">En curso actualmente — desliza para ver más</small>
            </div>
            <span className="dashboard-chip">
              <i className="bi bi-play-circle me-1" />
              {activosOrdenados.filter((c) => c.cupo_disponible > 0).length} con cupo
            </span>
          </div>
          <div className="p-3 pt-0">
            <CursosActivosCarousel cursos={activosOrdenados} proximas={proximas} />
          </div>
        </div>
      )}

      {/* Proximas aperturas sin inscritos */}
      {proximasSinInscritos.length > 0 && (
        <div className="card recep-section-card">
          <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
            <div>
              <h6 className="mb-0">Proximas aperturas</h6>
              <small className="text-muted">Sin inscritos aun — fechas disponibles</small>
            </div>
            <span className="dashboard-chip">
              <i className="bi bi-calendar-event me-1" />
              {proximasSinInscritos.length}
            </span>
          </div>
          <div className="p-3">
            <div className="row g-3">
              {proximasSinInscritos.map((p) => {
                const disp = getDispInfo(p.cupo_disponible, p.capacidad);
                return (
                  <div key={p.id} className="col-sm-6 col-md-4 col-xl-3">
                    <div className="recep-proxima-item" style={{ borderLeft: `3px solid ${disp.barColor}` }}>
                      <div className="fw-semibold mb-1" style={{ fontSize: "0.88rem", color: "var(--mg-primary)" }}>
                        {p.curso_nombre}
                      </div>
                      <div className="text-muted mb-1" style={{ fontSize: "0.74rem" }}>{p.promocion}</div>
                      <div className="text-muted mb-2" style={{ fontSize: "0.74rem" }}>
                        <i className="bi bi-calendar3 me-1" />
                        {formatDate(p.fecha_inicio)}
                      </div>
                      <span
                        className="badge rounded-pill mb-2"
                        style={{ background: disp.bg, color: disp.color, fontSize: "0.76rem" }}
                      >
                        {p.cupo_disponible} cupo{p.cupo_disponible !== 1 ? "s" : ""}
                      </span>
                      <div className="recep-curso-info">
                        {formatMoney(p.precio_base) && (
                          <span className="recep-info-item">
                            <i className="bi bi-tag me-1" />
                            {formatMoney(p.precio_base)}
                          </span>
                        )}
                        {Number(p.duracion_horas) > 0 && (
                          <span className="recep-info-item recep-info-item--muted">
                            <i className="bi bi-clock me-1" />
                            {p.duracion_horas}h
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
